import {Button, Checkbox, Modal, Stack, TextInput} from "@mantine/core";
import {useForm} from "@mantine/form";
import CalendarEventApi, { CalendarEvent } from "../../../../api/calendar/event/CalendarEventApi";
import {useEffect, useState} from "react";
import {showNotification} from "@mantine/notifications";
import {IconCheck, IconX} from "@tabler/icons-react";
import {DatePickerInput} from "@mantine/dates";
import DevicePickerDialog from "../../../picker-dialog/DevicePickerDialog";
import {Device, DeviceApi} from "../../../../api/device/DeviceApi";

export type UpdateCalendarEventDialogAttrs = {
    event: CalendarEvent | undefined
    justClose: () => void;
    closeAndUpdate: () => void;
}

export type UpdateEventModel = {
    date: Date | null;
    comment: string;
    endDate: Date | null;
}

// noinspection DuplicatedCode
function formatDate(date: Date|null) {
    if (!date) { return undefined }
    const year = date.getFullYear();
    let month: string|number = date.getMonth() + 1;
    if (month < 10) {
        month = '0' + month;
    }
    let day: string|number = date.getDate();
    if (day < 10) {
        day = '0' + day;
    }
    return `${year}-${month}-${day}`;
}

function UpdateCalendarEventDialog(attrs: UpdateCalendarEventDialogAttrs) {
    const [selectedDevice, setSelectedDevice] = useState<Device | null>();
    const [isPickOpen, setPickOpen] = useState<boolean>(false);
    const [loading, setLoading] = useState(false);
    const [addPrefix, setAddPrefix] = useState(true);

    const [startLoading, stopLoading] = [
        () => setLoading(true),
        () => setLoading(false)
    ];

    const form = useForm({
        mode: 'uncontrolled',
        initialValues: {
            date: null,
            comment: null,
            endDate: null,
        },
        validate: {
            comment: (c: string) => c.length < 2 ? 'Przynajmniej dwa znaki' : null
        }
    });

    const doSubmitSuccess = () => {
        showNotification({
            title: 'Zapisano wydarzenie',
            message: 'Wydarzenie zostało zaktualizowane',
            color: 'green',
            icon: <IconCheck/>
        });
    }

    const doSubmitError = () => {
        showNotification({
            title: 'Nie udało się zapisać wydarzenia',
            message: 'Jeśli problem nie ustąpi, proszę skontaktować się z administratorem',
            color: 'red',
            icon: <IconX/>
        });
    }

    const doSubmit = (values: UpdateEventModel) => {
        if (attrs.event === undefined) {
            throw new Error("Event is undefined. Could not submit.");
        }

        startLoading();

        const payload = {
            ...values,
            unit: selectedDevice?.unit!!,
            date: formatDate(values.date)!!,
            endDate: formatDate(values.endDate),
            addPrefixAutomatically: addPrefix,
        };

        CalendarEventApi.updateById(attrs.event.id, payload)
            .then(doSubmitSuccess)
            .then(attrs.closeAndUpdate)
            .catch(doSubmitError)
            .finally(stopLoading);
    }

    useEffect(() => {
        if (!attrs.event || !attrs.event.unit) {
            return;
        }

        DeviceApi.getByUnit(attrs.event.unit)
            .then(setSelectedDevice);
    }, [attrs.event]);

    useEffect(() => {
        if (!attrs.event) {
            return;
        }

        form.setValues({
            endDate: attrs.event.endDate ? new Date(attrs.event.endDate[0], attrs.event.endDate[1] - 1, attrs.event.endDate[2]) : null,
            comment: attrs.event.comment,
            date: new Date(attrs.event.date[0], attrs.event.date[1] - 1, attrs.event.date[2]),
        });

        setAddPrefix(attrs.event.addPrefixAutomatically);

        // eslint-disable-next-line
    }, [attrs.event]);

    return (
        <>
            <Modal
                opened={attrs.event !== undefined}
                onClose={attrs.justClose}
                title="Edycja wydarzenia"
            >
                <form
                    onSubmit={form.onSubmit(doSubmit)}
                >
                    <Stack>
                        <TextInput
                            withAsterisk
                            placeholder="Jednostka"
                            description={selectedDevice?.name}
                            value={selectedDevice?.unit}
                        />

                        <Button
                            children="Wyszukaj"
                            onClick={() => {setPickOpen(true)}}
                        />

                        <DatePickerInput
                            withAsterisk
                            placeholder="Data"
                            description={<div style={{color: 'red'}}>Zmiany nie zostaną przeniesione na raport z interwencji.</div> }
                            key={form.key('date')}
                            { ...form.getInputProps('date') }
                        />

                        <DatePickerInput
                            clearable={true}
                            placeholder="Data (Do)"
                            description={<div style={{color: 'red'}}>Zmiany nie zostaną przeniesione na raport z interwencji.</div> }
                            key={form.key('endDate')}
                            { ...form.getInputProps('endDate') }
                        />

                        <TextInput
                            withAsterisk
                            placeholder="Komentarz"
                            description="data/y będą dodane na raporcie końcowym"
                            key={form.key('comment')}
                            { ...form.getInputProps('comment') }
                        />

                        <Checkbox
                            label="Dodawaj dzień do komentarzy"
                            checked={addPrefix}
                            onChange={e => setAddPrefix(e.target.checked)}
                        />

                        <Button type="submit" loading={loading}>
                            Zapisz
                        </Button>
                    </Stack>
                </form>
            </Modal>

            <DevicePickerDialog
                device={selectedDevice}
                setDevice={setSelectedDevice}
                isOpen={isPickOpen}
                justClose={() => setPickOpen(false)}
            />
        </>
    );
}

export default UpdateCalendarEventDialog;